import React, {
  createContext,
  useMemo,
  useEffect,
  useCallback,
  useReducer,
  useRef,
  useState,
} from 'react'
import PropTypes from 'prop-types'
import { debounce, isEqual } from 'lodash'
import i18n from 'i18n'
import * as Sentry from '@sentry/react'

import setupProcessApi from 'api/setupProcesses'
import { useImmerReducerAsync } from 'hooks/useReducerAsync'
import {
  AssetClass,
  buildSetupPayload,
  CampaignGoal,
  Platform,
  prepareTargetingPayload,
  validateCreative,
  validateIntegrations,
  getLocaleForLanguage,
  SPECIAL_AD_CATEGORY,
} from 'api/models'
import { initStateObject, objectReducer, UPDATE } from 'reducers/default'
import { validateLocationTargeting } from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/targeting'
import { userReportsApi } from 'api/userReports'
import { TargetingContextProvider } from '../Edit/CampaignForm/Targeting/TargetingContext'
import useHasPermission from 'hooks/useHasPermission'
import { offsetVariationsToHandleRemovedCreativeFields } from './disabledVariations'
import { campaignSetupApi } from 'api/campaigns'
import { codifyAPIError } from 'utils/helpers'
import metaAuthorizationApi, { metaAssetsApi } from 'api/connect/meta'
import { actions } from './actionTypes'
import { reducer } from './reducer'
import {
  _checkDisabledVariationsIntegrity,
  buildTargetingPayload,
  calculateSetupCreativesFromUpdatedTCCreatives,
  getDisabledVariationHashes,
  hasCustomAudience,
} from './util'
import { useSearchParam } from 'hooks/useSearchParams'
import { EUROPEAN_COUNTRIES } from 'components/Recommendations/EventCampaigns/Steps/Setup/constants'
/** @typedef {import('./types.js').SetupState} SetupState */
/** @typedef {import('./types.js').CampaignSetupDispatchContext} CampaignSetupDispatchContextType */

/**
 * @type {React.Context<SetupState>}
 */
const CampaignSetupContext = createContext(null)
/**
 * @type {React.Context<CampaignSetupDispatchContextType>}
 */
const CampaignSetupDispatchContext = createContext(null)
export const CurrentStepContext = createContext(null)
export const CurrentStepDispatchContext = createContext(null)
export const AvailableTasteClustersContext = createContext(null)

export function CampaignSetupStepProvider({ children }) {
  const searchParam = useSearchParam()
  const step = searchParam.get('step')
  const [currentStepState, dispatchCurrentStep] = useReducer(objectReducer, {
    ...initStateObject,
    content: {
      currentStep: step ? parseInt(step, 10) : 1,
      currentCompletedStep: null,
    },
  })

  const setCurrentStep = useCallback(
    (step, completedStep = null) => {
      const payload = completedStep ? { currentCompletedStep: completedStep } : {}
      dispatchCurrentStep({ type: UPDATE, payload: { ...payload, currentStep: step } })
    },
    [dispatchCurrentStep],
  )

  const goToNextStep = useCallback(() => {
    if (currentStepState.content.currentStep === 5) return

    const { currentStep, currentCompletedStep } = currentStepState.content
    setCurrentStep(currentStep + 1, currentCompletedStep + 1)
  }, [currentStepState, setCurrentStep])

  const goToPrevStep = useCallback(() => {
    if (currentStepState.content.currentStep === 1) return

    const { currentStep, currentCompletedStep } = currentStepState.content
    setCurrentStep(currentStep - 1, currentCompletedStep - 1)
  }, [currentStepState, setCurrentStep])

  return (
    <CurrentStepContext.Provider value={currentStepState.content}>
      <CurrentStepDispatchContext.Provider value={{ setCurrentStep, goToNextStep, goToPrevStep }}>
        {children}
      </CurrentStepDispatchContext.Provider>
    </CurrentStepContext.Provider>
  )
}

CampaignSetupStepProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

/**
 * Provides the context for available taste clusters.
 * For now, it's mainly used to keep track of the open taste clusters.
 *
 * @component
 */
function AvailableTasteClustersContextProvider({ children }) {
  const openTCs = useState([])

  return (
    <AvailableTasteClustersContext.Provider value={{ openTCs }}>
      {children}
    </AvailableTasteClustersContext.Provider>
  )
}

AvailableTasteClustersContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default function CampaignSetupProvider({ eventId, campaign, children }) {
  const hasWaveAccess = useHasPermission(Permissions.wave)
  const initialState = useMemo(() => {
    if (!eventId) return null

    if (!campaign || !campaign.latest_campaign_setup) {
      return {
        ..._initialState,
        eid: eventId,
      }
    }

    const creative = campaign.latest_campaign_setup.creative

    return {
      ..._initialState,
      eid: eventId,
      isPublished: true,
      setup: {
        ...campaign.latest_campaign_setup,
        creatives: [
          {
            ...creative,
            tc: campaign.tc,
            tc_run_id: campaign.tc_run_id,
            audience_id: campaign.latest_campaign_setup.audience_id || creative.audience_id,
            saved_audience_id: campaign.latest_campaign_setup.saved_audience_id || creative.saved_audience_id,
          },
        ],
      },
    }
  }, [eventId, campaign])

  const [state, dispatch] = useImmerReducerAsync(reducer, initialState, asyncActionHandlers)
  const { eid, setup, accounts, loading, componentErrors, hasPendingMedia, isPublished } = state
  const shouldAutosync = useMemo(() => !campaign?.latest_campaign_setup, [campaign])
  const [debouncedPending, setPendingDebounce] = useState(false)
  const disabledVariationsHashRef = useRef(isPublished ? getDisabledVariationHashes(setup) : {})

  useEffect(() => {
    if (!setup) {
      disabledVariationsHashRef.current = {}
      return
    }
  }, [setup])

  const fetchSetup = useCallback(() => {
    const action = { type: actions.fetchAsync, payload: { eid } }
    fetchSetupAsync({ dispatch })(action).then(({ success, setup }) => {
      if (success && setup) {
        disabledVariationsHashRef.current = getDisabledVariationHashes(setup)
      }
    })
  }, [dispatch, eid])

  const debouncedSync = useRef(
    debounce((setup, callback = () => {}) => {
      setPendingDebounce(false)
      if (!shouldAutosync) {
        return
      }

      const action = { type: actions.syncAsync, payload: { eid, setup } }
      syncSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    }, 1000),
  )

  const sync = useCallback(
    (setup, callback = () => {}) => {
      const action = { type: actions.syncAsync, payload: { setup } }
      syncSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state],
  )

  const startSetup = useCallback(
    (config, callback = () => {}) => {
      const action = {
        type: actions.createAsync,
        payload: { eid: state.eid, config },
      }
      createSetupAsync({ dispatch })(action).then(callback)
    },
    [dispatch, state],
  )

  const fetchAccounts = useCallback(() => {
    const action = { type: actions.fetchAccountsAsync }
    fetchAccountsAsync({ dispatch })(action)
  }, [dispatch])

  const enrichAccounts = useCallback(
    (accounts, callback) => {
      const action = { type: actions.validateAccountsAsync, payload: { accounts } }
      enrichAccountsAsync({ dispatch })(action).then(callback)
    },
    [dispatch],
  )

  const enrichAsset = useCallback(
    (asset) => {
      const { asset_id, asset_class, platform } = asset
      const account = accounts.find(
        (x) => x.asset_id === asset_id && x.asset_class === asset_class && x.platform === platform,
      )

      return {
        ...asset,
        ...account,
      }
    },
    [accounts],
  )

  const getAdAccount = useCallback(() => {
    if (!setup) {
      return null
    }

    const selectedAdAccount = setup.integration_details?.find(
      (x) => x.asset_class === AssetClass.ad_account && x.platform === Platform.facebook,
    )
    if (!selectedAdAccount) {
      return null
    }

    return enrichAsset(selectedAdAccount)
  }, [setup, enrichAsset])

  const enrichAccountsAndReconfigure = useCallback(
    (config, callback = () => {}) => {
      const action = {
        type: actions.enrichAccountsAndReconfigureAsync,
        payload: { eid: state.eid, config },
      }
      enrichAccountsAndReconfigureAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state],
  )

  const reconfigureSetup = useCallback(
    (config, callback = () => {}) => {
      const action = {
        type: actions.reconfigureAsync,
        payload: { eid: state.eid, config },
      }
      reconfigureSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state],
  )

  const cancelSetup = useCallback(
    (callback = () => {}) => {
      const action = {
        type: actions.deleteAsync,
        payload: { eid: state.eid },
      }
      deleteSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state],
  )

  const setHasPendingMedia = useCallback(
    (hasPendingMedia) => {
      dispatch({ type: actions.setHasPendingMedia, payload: hasPendingMedia })
    },
    [dispatch],
  )

  const overwriteTCCreatives = useCallback(
    ({ tc, tc_run_id, creatives, creativeAction }) => {
      const { removedFields } = creativeAction
      if (removedFields) {
        creatives.excluded_variations = offsetVariationsToHandleRemovedCreativeFields(
          removedFields,
          setup.creatives,
          tc,
        )
      }

      dispatch({
        type: actions.setTCCreatives,
        payload: { tc, tc_run_id, ...creatives },
      })

      const newCreatives = calculateSetupCreativesFromUpdatedTCCreatives(
        setup.creatives,
        tc,
        tc_run_id,
        creatives,
      )

      if (shouldAutosync) {
        debouncedSync.current.cancel()
        setPendingDebounce(true)
        debouncedSync.current({ ...setup, creatives: newCreatives })
      }
    },
    [dispatch, setup, shouldAutosync],
  )

  const updateDisabledAdVariations = useCallback(
    (disabledVariations, callback = () => {}, autoSync = true) => {
      const creatives = setup.creatives.map((creative) => ({
        ...creative,
        excluded_variations: disabledVariations[creative.tc],
      }))

      dispatch({
        type: actions.setCreatives,
        payload: creatives,
      })

      if (autoSync) {
        sync({ ...setup, creatives }, ({ success, data }) => {
          if (success) {
            disabledVariationsHashRef.current = getDisabledVariationHashes({ ...setup, creatives })
          }
          callback({ success, data })
        })
      }
    },
    [dispatch, setup, sync],
  )

  const overwriteTCTargeting = useCallback(
    (tcTargeting, callback = () => {}) => {
      const action = {
        type: actions.updateTCTargetingAsync,
        payload: { ...tcTargeting, shouldSync: !isPublished },
      }

      updateTCTargetingAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state, isPublished],
  )

  const updateAgeTargeting = useCallback(
    (min, max, callback = () => {}) => {
      // updates age targeting for all targeting tc objects
      const targeting = setup.targeting.map((x) => ({
        ...x,
        ageRange: {
          min: min !== undefined ? min : x.ageRange?.min || 18,
          max: max !== undefined ? max : x.ageRange?.max || 65,
        },
      }))
      const action = {
        type: actions.updateTargetingAsync,
        payload: { targeting, shouldSync: !isPublished },
      }
      updateTargetingAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state, isPublished, setup],
  )

  const checkDisabledVariationsIntegrity = useCallback(
    (currentDisabledVariations = null) => {
      const creatives = setup.creatives
      const integrityHashes = disabledVariationsHashRef.current

      return _checkDisabledVariationsIntegrity(
        creatives,
        integrityHashes,
        currentDisabledVariations,
      )
    },
    [setup],
  )

  const updateDsaFields = useCallback(
    (fields, callback = () => {}) => {
      if (!setup) return

      const nextSetup = {
        ...setup,
        ...(fields.dsa_payor !== undefined ? { dsa_payor: fields.dsa_payor } : {}),
        ...(fields.dsa_beneficiary !== undefined
          ? { dsa_beneficiary: fields.dsa_beneficiary }
          : {}),
      }

      // Optimistically update local state so UI reflects latest values
      dispatch({ type: actions.setupLoaded, payload: nextSetup })

      // Sync with delay
      if (shouldAutosync) {
        debouncedSync.current.cancel()
        setPendingDebounce(true)
        debouncedSync.current(nextSetup, callback)
      }
    },
    [setup, dispatch, shouldAutosync],
  )

  const publishSetup = useCallback(
    (callback = () => {}) => {
      // force re-validation by fetching the setup
      let action = { type: actions.fetchAsync, payload: { eid } }
      fetchSetupAsync({ dispatch })(action).then(({ success, setup }) => {
        if (success) {
          enrichAccountsAsync({ dispatch })({
            type: actions.validateAccountsAsync,
            payload: { accounts: setup.integration_details },
          }).then(({ success, hasInvalidAccounts }) => {
            if (success && !hasInvalidAccounts) {
              const isIntegrationsValid = validateIntegrations(setup.integration_details, accounts)
              const isCreativesValid = setup.creatives.every(
                (x) => validateCreative(x, { setupOnly: false }).valid,
              )
              const isTargetingValid = setup.targeting.every((x) => {
                const creativeForTc = setup.creatives?.find(
                  (c) => c.tc === x.tc && c.tc_run_id === x.tc_run_id,
                )

                return validateLocationTargeting(
                  setup.targeting,
                  x.tc,
                  x.tc_run_id,
                  hasCustomAudience(x.tc, setup.creatives),
                  !!creativeForTc?.saved_audience_id,
                ).valid
              })

              if (isIntegrationsValid && isCreativesValid && isTargetingValid) {
                publishSetupAsync({ dispatch, getState: () => state })().then(callback)
              } else {
                Sentry.captureMessage('Invalid setup', {
                  extra: {
                    eid,
                    isIntegrationsValid,
                    isCreativesValid,
                    isTargetingValid,
                  },
                })

                callback({ success: false, reason: 'INVALID_SETUP' })
              }
            } else {
              callback({ success: false, reason: 'INVALID_ACCOUNTS' })
            }
          })
        }
      })
    },
    [dispatch, state, eid, accounts],
  )

  const editPublishedSetup = useCallback(
    (callback = () => {}, disabledVariations = null) => {
      const action = {
        type: actions.editAsync,
        payload: { tc: campaign.tc, tc_run_id: campaign.tc_run_id, disabledVariations },
      }
      editPublishedSetupAsync({ dispatch, getState: () => state })(action).then(callback)
    },
    [dispatch, state, campaign],
  )

  const setError = useCallback(
    (error) => {
      dispatch({ type: actions.error, payload: error })
    },
    [dispatch],
  )

  const setComponentError = useCallback(
    (key, error) => {
      dispatch({ type: actions.setComponentError, payload: { key, error } })
    },
    [dispatch],
  )


  const getAgeTargeting = useCallback(() => {
    const defaultAgeRange = { min: 18, max: 65 }
    if (!setup || !Array.isArray(setup.targeting)) {
      return defaultAgeRange
    }
    // return the first targeting object that has ageRange defined
    const targeting = setup.targeting.find((x) => x.ageRange && x.ageRange.min !== undefined && x.ageRange.max !== undefined)
    if (!targeting) {
      return defaultAgeRange
    }
    return {
      min: targeting.ageRange.min,
      max: targeting.ageRange.max,
    }
  }, [setup])

  const isAgeRangeDisabled = useCallback(
    (event) => {
      if (!event || !event.special_ad_category) {
        return false
      }

      const isEuropeanCountry = (() => {
        if (!setup?.targeting || !Array.isArray(setup.targeting) || setup.targeting.length === 0) {
          return false
        }

        const allCountryCodes = new Set()

        setup.targeting.forEach((tcTargeting) => {
          if (tcTargeting.rangeLocations?.included && Array.isArray(tcTargeting.rangeLocations.included)) {
            tcTargeting.rangeLocations.included.forEach((locationItem) => {
              if (locationItem?.location?.country_code) {
                allCountryCodes.add(locationItem.location.country_code)
              }
            })
          }

          if (tcTargeting.postalCodes?.countries && typeof tcTargeting.postalCodes.countries === 'object') {
            Object.keys(tcTargeting.postalCodes.countries).forEach((countryCode) => {
              allCountryCodes.add(countryCode)
            })
          }
        })

        if (allCountryCodes.size === 0) {
          return false
        }

        return Array.from(allCountryCodes).every((countryCode) =>
          EUROPEAN_COUNTRIES.includes(countryCode),
        )
      })()

      // Age range is disabled if:
      // 1. Event has employment or housing special ad category
      // 2. Event has financial or political special ad category AND targeting is not European
      return (
        event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.employment) ||
        event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.housing) ||
        (!isEuropeanCountry && event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.financial)) ||
        (!isEuropeanCountry && event.special_ad_category?.includes(SPECIAL_AD_CATEGORY.political))
      )
    },
    [setup],
  )

  const isTargetingValid = useMemo(() => {
    if (!Array.isArray(setup?.targeting)) return false

    const targeting = setup.targeting

    return targeting.every((x) => {
      const creativeForTc = setup.creatives?.find(
        (c) => c.tc === x.tc && c.tc_run_id === x.tc_run_id,
      )

      const isValid = validateLocationTargeting(
        targeting,
        x.tc,
        x.tc_run_id,
        hasCustomAudience(x.tc, setup.creatives),
        !!creativeForTc?.saved_audience_id,
      ).valid

      return isValid
    })
  }, [setup])

  const isIntegrationsValid = useMemo(() => {
    if (Object.values(componentErrors).some((x) => x)) {
      return false
    }
    return setup && validateIntegrations(setup.integration_details, accounts)
  }, [setup, accounts, componentErrors])

  const isCreativesValid = useMemo(
    () => setup && setup.creatives.every((x) => validateCreative(x, { setupOnly: false }).valid),
    [setup],
  )

  const validatePlacementTypes = (media, hasLeadForm, hasLanguageVariations) => {
    // Only validate if lead form or language variations are present
    if (!hasLeadForm && !hasLanguageVariations) {
      return null
    }
    
    if (!media || media.length === 0) {
      return null
    }
    
    const groups = new Map()
    media.forEach((file) => {
      const groupId = file.placement_group_id || `standalone-${file.id}`
        
      if (!groups.has(groupId)) {
        groups.set(groupId, {
          hasFeed: false,
          hasReel: false,
        })
      }
        
      const group = groups.get(groupId)
      if (file.placement_type === 'feed') {
        group.hasFeed = true
      } else if (file.placement_type === 'reel') {
        group.hasReel = true
      }
    })
    
    const groupStates = Array.from(groups.values()).map((group) => {
      if (group.hasFeed && group.hasReel) return 'both'
      if (group.hasFeed && !group.hasReel) return 'feed-only'
      if (!group.hasFeed && group.hasReel) return 'reel-only'
      return 'none'
    })
    
    const uniqueStates = new Set(groupStates.filter(state => state !== 'none'))
      
    const isValid = uniqueStates.size <= 1
    
    if (!isValid) {
      return hasLeadForm ? 'leadForm' : 'languageVariation'
    }
    
    return null
  }

  // Compute media placement errors for all clusters
  const mediaPlacementErrors = useMemo(() => {
    if (!setup || !setup.creatives) return {}
    
    const errors = {}
    const hasLeadForm = !!setup.lead_form_id || setup.custom_event_type === 'LEAD'

    setup.creatives.forEach((creative) => {
      const hasLanguageVariations = Object.keys(creative.localizations || {}).length > 0
      
      if (hasLeadForm || hasLanguageVariations) {
        const allMedia = [
          ...(creative.media || []),
          ...(creative.placement_media || []),
        ]
        
        const placementError = validatePlacementTypes(allMedia, hasLeadForm, hasLanguageVariations)

        if (placementError) {
          const errorKey = `Recommendations.campaign.setup.media.errors.${placementError}`
          errors[creative.tc] = i18n.t(errorKey)
        }
      }
    })

    return errors
  }, [setup])

  // Get media placement error for a specific cluster
  const getMediaPlacementError = useCallback(
    (tc, tc_run_id) => {
      if (!setup || !setup.creatives) return null
      
      const creative = setup.creatives.find((x) => x.tc === tc && x.tc_run_id === tc_run_id)
      if (!creative) return null
      
      return mediaPlacementErrors[tc] || null
    },
    [setup, mediaPlacementErrors],
  )

  // Check if any cluster has media placement error
  const hasMediaPlacementErrors = useMemo(
    () => Object.keys(mediaPlacementErrors).length > 0,
    [mediaPlacementErrors],
  )

  const isClusterValid = useCallback(
    (tc, tc_run_id) => {
      if (!setup || !setup.creatives) return false
      
      const creative = setup.creatives.find((x) => x.tc === tc && x.tc_run_id === tc_run_id)
      if (!creative) return false

      const isCreativeValid = validateCreative(creative, { setupOnly: false }).valid
      if (!isCreativeValid) return false

      if (!Array.isArray(setup.targeting)) return false
      
      const isTargetingValid = validateLocationTargeting(
        setup.targeting,
        tc,
        tc_run_id,
        hasCustomAudience(tc, setup.creatives),
        !!creative.saved_audience_id,
      ).valid
      
      if (!isTargetingValid) return false

      // Check media placement validation
      if (getMediaPlacementError(tc, tc_run_id)) {
        return false
      }
      
      return true
    },
    [setup, getMediaPlacementError],
  )

  useEffect(() => {
    if (!setup) {
      return
    }

    const disabledVariations = setup.creatives.reduce((acc, c) => {
      acc[c.tc] = c.excluded_variations || []
      return acc
    }, {})

    if (!isEqual(disabledVariations, state.disabledVariations)) {
      dispatch({ type: actions.setSetupDisabledVariations, payload: disabledVariations })
    }
  }, [setup, dispatch, state.disabledVariations])

  const isStepValid = useCallback(
    (step) => {
      switch (step) {
        case 1:
          return true
        case 2:
          return isIntegrationsValid && !loading
        case 3:
          return (
            isIntegrationsValid &&
            isCreativesValid &&
            isTargetingValid &&
            !hasPendingMedia &&
            !debouncedPending &&
            !loading &&
            !hasMediaPlacementErrors
          )
        case 4:
          return (
            isIntegrationsValid &&
            isCreativesValid &&
            isTargetingValid &&
            hasWaveAccess &&
            !loading &&
            !debouncedPending &&
            !hasMediaPlacementErrors
          )
        default:
          return false
      }
    },
    [
      hasWaveAccess,
      isIntegrationsValid,
      isCreativesValid,
      isTargetingValid,
      hasPendingMedia,
      debouncedPending,
      loading,
      hasMediaPlacementErrors,
    ],
  )

  useEffect(() => {
    const fetchMetaStatus = async () => {
      const resp = await metaAuthorizationApi.getStatus()
      if (resp.error) {
        setError(codifyAPIError(resp))
      }

      const { partner_connection_type } = resp.data ?? {}
      dispatch({
        type: actions.setMetaStatus,
        payload: partner_connection_type,
      })
    }

    fetchMetaStatus()
  }, [dispatch, setError])

  useEffect(() => {
    if (eventId && !campaign) {
      fetchSetup()
    }
  }, [eventId, fetchSetup, campaign])

  useEffect(() => {
    dispatch({ type: actions.setCreativesValid, payload: isCreativesValid })
  }, [dispatch, isCreativesValid])

  useEffect(() => {
    dispatch({ type: actions.setTargetingValid, payload: isTargetingValid })
  }, [dispatch, isTargetingValid])

  useEffect(() => {
    dispatch({ type: actions.setIntegrationsValid, payload: isIntegrationsValid })
  }, [dispatch, isIntegrationsValid])

  return (
    <CampaignSetupStepProvider>
      <CampaignSetupContext.Provider value={state}>
        <AvailableTasteClustersContextProvider>
          <CampaignSetupDispatchContext.Provider
            value={{
              dispatch,
              fetchSetup,
              startSetup,
              cancelSetup,
              checkDisabledVariationsIntegrity,
              publishSetup,
              isStepValid,
              reconfigureSetup,
              overwriteTCCreatives,
              overwriteTCTargeting,
              updateDisabledAdVariations,
              editPublishedSetup,
              setHasPendingMedia,
              fetchAccounts,
              enrichAccountsAndReconfigure,
              enrichAccounts,
              enrichAsset,
              getAdAccount,
              setError,
              setComponentError,
              getMediaPlacementError,
              getAgeTargeting,
              updateAgeTargeting,
              isAgeRangeDisabled,
              isClusterValid,
              updateDsaFields,
            }}
          >
            <TargetingContextProvider>{children}</TargetingContextProvider>
          </CampaignSetupDispatchContext.Provider>
        </AvailableTasteClustersContextProvider>
      </CampaignSetupContext.Provider>
    </CampaignSetupStepProvider>
  )
}

CampaignSetupProvider.propTypes = {
  eventId: PropTypes.string.isRequired,
  campaign: PropTypes.shape({
    tc: PropTypes.string.isRequired,
    tc_run_id: PropTypes.number.isRequired,
    latest_campaign_setup: PropTypes.object,
  }),
  children: PropTypes.node.isRequired,
}

export const useCampaignSetup = () => {
  const context = React.useContext(CampaignSetupContext)

  if (context === undefined) {
    throw new Error('useCampaignSetup must be used within a CampaignSetupProvider')
  }

  return context
}

export const useCampaignSetupDispatch = () => {
  const context = React.useContext(CampaignSetupDispatchContext)

  if (context === undefined) {
    throw new Error('useCampaignSetupDispatch must be used within a CampaignSetupProvider')
  }

  return context
}

export const useCurrentStep = () => {
  const context = React.useContext(CurrentStepContext)

  if (context === undefined) {
    throw new Error('useCurrentStep must be used within a CampaignSetupProvider')
  }

  return context
}

export const useCurrentStepDispatch = () => {
  const context = React.useContext(CurrentStepDispatchContext)

  if (context === undefined) {
    throw new Error('useCurrentStepDispatch must be used within a CampaignSetupProvider')
  }

  return context
}

const _initialState = {
  loading: false,
  hasPendingMedia: false,
  error: null,
  componentErrors: {}, // used to store errors for specific components
  lastSyncedAt: null,
  eid: null,
  setup: null,
  isCreativesValid: false,
  isIntegrationsValid: false,
  isTargetingValid: false,
  disabledVariations: {},
  accounts: [],
  metaConnectionStatus: null,
}

export const asyncActionHandlers = {
  [actions.syncAsync]: syncSetupAsync,
  [actions.fetchAsync]: fetchSetupAsync,
  [actions.createAsync]: createSetupAsync,
  [actions.reconfigureAsync]: reconfigureSetupAsync,
  [actions.deleteAsync]: deleteSetupAsync,
  [actions.updateTCTargetingAsync]: updateTCTargetingAsync,
  [actions.publicAsync]: publishSetupAsync,
  [actions.editAsync]: editPublishedSetupAsync,
  [actions.fetchAccountsAsync]: fetchAccountsAsync,
  [actions.validateAccountsAsync]: enrichAccountsAsync,
  [actions.enrichAccountsAndReconfigureAsync]: enrichAccountsAndReconfigureAsync,
}

export function fetchAccountsAsync({ dispatch }) {
  return async () => {
    dispatch({ type: actions.loading })
    const response = await metaAssetsApi.listAssets({ is_enabled: true })
    if (!response.error) {
      dispatch({
        type: actions.setAccounts,
        payload: response.data.map((x) => ({ ...x, valid: !!x.is_active })),
      })
      return { success: true, data: response.data }
    }

    handleError(dispatch, response)

    return { success: false }
  }
}

function enrichAccountsAsync({ dispatch }) {
  return async (action) => {
    const { accounts } = action.payload
    dispatch({ type: actions.loading })

    const response = await metaAssetsApi.enrichAssets(
      accounts
        .filter((x) => !!x.id)
        .map((x) => ({
          id: Number(x.id),
          asset_id: x.asset_id,
          asset_class: x.asset_class,
          platform: x.platform,
        })),
    )

    if (response.error) {
      handleError(dispatch, response)
      return { success: false }
    }

    const newAccounts = accounts.map((account) => {
      const enrichedAccount = response.data.find(
        (x) =>
          x.asset_id === account.asset_id &&
          x.asset_class === account.asset_class &&
          x.platform === account.platform,
      )
      if (!enrichedAccount) {
        return { ...account, valid: false }
      }

      return { ...account, ...enrichedAccount, valid: enrichedAccount.valid }
    })

    const hasInvalidAccounts = newAccounts.some((x) => !x.valid)

    dispatch({ type: actions.patchAccounts, payload: newAccounts })
    return { success: true, data: response.data, hasInvalidAccounts }
  }
}

function enrichAccountsAndReconfigureAsync({ dispatch, getState }) {
  return async (action) => {
    // validate accounts, upon success, and if all accounts are valid, reconfigure the setup
    const { eid, config } = action.payload
    let { integration_details: accounts } = config
    accounts = accounts || getState().setup.integration_details

    if (!accounts) {
      const reconfigureAction = { type: actions.reconfigureAsync, payload: { eid, config } }
      return reconfigureSetupAsync({ dispatch, getState })(reconfigureAction)
    }

    const validateAction = { type: actions.validateAccountsAsync, payload: { accounts } }
    const response = await enrichAccountsAsync({ dispatch })(validateAction)

    if (!response.success) {
      return { success: false }
    }

    const { hasInvalidAccounts, data: enrichedPartnerAccounts } = response

    // enrich selected accounts
    const newIntegrations = accounts.map((x) => {
      const enrichedAccount = enrichedPartnerAccounts.find(
        (y) =>
          y.asset_id === x.asset_id && y.asset_class === x.asset_class && y.platform === x.platform,
      )
      if (!enrichedAccount) {
        return x
      }

      return { ...x, ...enrichedAccount }
    })

    const reconfigureAction = {
      type: actions.reconfigureAsync,
      payload: { eid, config: { ...config, integration_details: newIntegrations } },
    }
    // Update the setup anyway, regardless of the account validation result
    // This will make sure that the state on screen matches the actual state
    const result = await reconfigureSetupAsync({ dispatch, getState })(reconfigureAction)

    if (hasInvalidAccounts) {
      dispatch({ type: actions.error, payload: 'common.genericError' })
      return { success: false, hasInvalidAccounts }
    } else {
      return result
    }
  }
}

function deleteSetupAsync({ dispatch, getState }) {
  return async (action) => {
    const { eid, cancel } = action.payload
    const {
      setup: { id },
    } = getState()
    dispatch({ type: actions.loading })

    const response = await setupProcessApi.deleteAsync(eid, cancel)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: null })
      await userReportsApi.deleteUserReports({ setup_process_ids: [id] })
      dispatch({ type: actions.fetchAsync, payload: { eid } })

      return { success: true }
    } else {
      handleError(dispatch, response)

      return { success: false }
    }
  }
}

function createSetupAsync({ dispatch }) {
  return async (action) => {
    const { eid, config } = action.payload
    const defaultLanguage = i18n.language

    const payload = buildSetupPayload(
      {
        ...config,
        creatives: config.creatives.map((creative) => ({
          ...creative,
          language: creative.language || defaultLanguage,
        })),
      },
      { setupOnly: true, setDefaultTargeting: true },
    )

    dispatch({ type: actions.loading })

    const response = await setupProcessApi.createAsync(eid, payload)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return {
        success: true,
        data: response.data,
      }
    } else {
      handleError(dispatch, response)

      return {
        success: false,
        data: response.data,
      }
    }
  }
}

function fetchSetupAsync({ dispatch }) {
  return async (action) => {
    const { eid } = action.payload
    dispatch({ type: actions.loading })

    const response = await setupProcessApi.fetchAsync(eid)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return {
        success: true,
        setup: response.data,
      }
    } else if (response.status === 404) {
      dispatch({ type: actions.setupLoaded, payload: null })

      return {
        success: true,
        setup: null,
      }
    } else {
      handleError(dispatch, response)

      return { success: false }
    }
  }
}

function syncSetupAsync({ dispatch, getState }) {
  return async (action) => {
    dispatch({ type: actions.loading })
    let { eid, setup } = getState()
    if (action?.payload) {
      setup = action.payload.setup
    }

    const payload = buildSetupPayload(setup)

    const response = await setupProcessApi.updateAsync(eid, payload)
    if (response.status === 200) {
      dispatch({ type: actions.setupSynced, payload: Date.now() })

      return {
        success: true,
        data: response.data,
      }
    }

    handleError(dispatch, response)

    return { success: false }
  }
}

function reconfigureSetupAsync({ dispatch, getState }) {
  return async (action) => {
    const { eid, config } = action.payload
    const { setup } = getState()
    const { creatives: currentCreatives } = setup
    const goal = config.goal || setup.goal

    let leadFormId = 'lead_form_id' in config ? config.lead_form_id : setup.lead_form_id
    let conversionId = 'conversion_id' in config ? config.conversion_id : setup.conversion_id
    let customEventType =
      'custom_event_type' in config
        ? config.custom_event_type
        : setup.custom_event_type || 'PURCHASE'

    // Nullify leadFormId if goal isn't utilization
    if (goal !== CampaignGoal.utilization) {
      leadFormId = null
    }

    // Determine if lead form was added or removed
    const hadLeadForm = !!setup.lead_form_id
    const hasLeadForm = !!leadFormId

    if (!hasLeadForm && hadLeadForm) {
      // Lead form removed
      conversionId = null
      customEventType = 'PURCHASE'
    } else if (hasLeadForm && !hadLeadForm) {
      // Lead form added
      customEventType = 'LEAD'
    }

    if (!customEventType) {
      // make sure custom_event_type is always set
      customEventType = hasLeadForm ? 'LEAD' : 'PURCHASE'
    }

    const language = currentCreatives.length > 0 ? currentCreatives[0].language : i18n.language

    const newCreatives = config.creatives.map((c) => {
      const currentCreative = currentCreatives.find(
        (cc) => cc.tc === c.tc && cc.tc_run_id === c.tc_run_id,
      )
      if (currentCreative) {
        return { ...currentCreative, ...c }
      }

      // a new creative
      return { ...c, language }
    })

    const targeting = setup.targeting.filter((x) => config.creatives.some((y) => y.tc === x.tc))

    dispatch({ type: actions.loading })

    const payload = buildSetupPayload(
      {
        ...setup,
        ...config,
        creatives: newCreatives,
        targeting,
        lead_form_id: leadFormId,
        conversion_id: conversionId,
        custom_event_type: customEventType,
      },
      { setupOnly: true, setDefaultTargeting: true },
    )
    const response = await setupProcessApi.updateAsync(eid, payload)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return {
        success: true,
        data: response.data,
      }
    } else {
      handleError(dispatch, response)

      return {
        success: false,
        data: response.data,
      }
    }
  }
}

function updateTCTargetingAsync({ dispatch, getState }) {
  return async (action) => {
    const { eid, setup } = getState()
    const { shouldSync, ...targetingPayload } = action.payload
    const newTargeting = buildTargetingPayload(setup, targetingPayload)
    dispatch({ type: actions.setTargeting, payload: newTargeting })

    if (!shouldSync) {
      return { success: true }
    }

    const payload = buildSetupPayload(
      { ...setup, targeting: newTargeting },
      { setupOnly: true, setDefaultTargeting: true },
    )

    const response = await setupProcessApi.updateAsync(eid, payload)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return {
        success: true,
        data: response.data,
      }
    }

    handleError(dispatch, response)

    return {
      success: false,
      data: response.data,
    }
  }
}

function updateTargetingAsync({ dispatch, getState }) {
  return async (action) => {
    const { eid, setup } = getState()
    const { targeting, shouldSync } = action.payload
    dispatch({ type: actions.setTargeting, payload: targeting })
    if (!shouldSync) {
      return { success: true }
    }
    dispatch({ type: actions.loading })
    const payload = buildSetupPayload(
      { ...setup, targeting },
      { setupOnly: true, setDefaultTargeting: true },
    )
    const response = await setupProcessApi.updateAsync(eid, payload)
    dispatch({ type: actions.loading, payload: false })
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })
      return {
        success: true,
        data: response.data,
      }
    }

    handleError(dispatch, response)
    return {
      success: false,
      data: response.data,
    }
  }
}

function publishSetupAsync({ dispatch, getState }) {
  return async () => {
    const { eid } = getState()

    dispatch({ type: actions.loading })

    const response = await setupProcessApi.publishAsync(eid)
    if (response.status === 200) {
      dispatch({ type: actions.setupLoaded, payload: response.data })

      return { success: true }
    }

    if (response.status === 400 || response.status === 422) {
      const errorResponse = response.error?.response?.data || {}
      const errorMessage = errorResponse.message || response.error?.message || 'Campaign publish error'
      const errorCode = errorResponse.error_code || null
      const validation = errorResponse.validation || {}
      
      Sentry.captureMessage('Campaign publish failed with 400/422', {
        level: 'error',
        tags: {
          eventId: eid,
          statusCode: response.status,
          errorType: 'campaign_publish_error',
          errorCode: errorCode || 'unknown',
        },
        extra: {
          eventId: eid,
          status: response.status,
          errorMessage,
          errorCode,
          validation: {
            valid: validation.valid,
            errors: validation.errors || [],
            warnings: validation.warnings || [],
          },
          fullErrorResponse: errorResponse,
        },
      })
    }

    handleError(dispatch, response)

    return { success: false }
  }
}

function editPublishedSetupAsync({ dispatch, getState }) {
  return async ({ payload: { tc, tc_run_id, disabledVariations } }) => {
    const { eid, setup } = getState()
    dispatch({ type: actions.loading })

    const creative = setup.creatives.find((c) => c.tc === tc && c.tc_run_id === tc_run_id)
    const creatives = {
      ...creative,
      body: creative.body.filter(Boolean),
      headline: creative.headline.filter(Boolean),
      destination_url: creative.destination_url.filter(Boolean),
      default_locale: getLocaleForLanguage(creative.language),
      localizations: creative.localizations || {},
    }

    if (disabledVariations) {
      creatives.excluded_variations = disabledVariations[tc]
    }

    if (!creatives.language) {
      creatives.language = i18n.language
    }

    const targeting = prepareTargetingPayload(setup.targeting, [creatives])[0]
    const { error, status } = await campaignSetupApi.updateAsync(eid, {
      tc: tc,
      tc_run_id: tc_run_id,
      creatives,
      targeting,
      audience_id: creatives.audience_id,
      saved_audience_id: creatives.saved_audience_id,
    })

    if (!error) {
      dispatch({ type: actions.setupLoaded, payload: null })

      return { success: true }
    }

    if (status === 400 || status === 422) {
      const errorResponse = error?.response?.data || {}
      const errorMessage = errorResponse.message || error?.message || 'Campaign edit error'
      const errorCode = errorResponse.error_code || null
      const validation = errorResponse.validation || {}
      
      Sentry.captureMessage('Campaign edit failed with 400/422', {
        level: 'error',
        tags: {
          eventId: eid,
          statusCode: status,
          errorType: 'campaign_edit_error',
          errorCode: errorCode || 'unknown',
        },
        extra: {
          eventId: eid,
          tc,
          tc_run_id,
          status,
          errorMessage,
          errorCode,
          validation: {
            valid: validation.valid,
            errors: validation.errors || [],
            warnings: validation.warnings || [],
          },
          fullErrorResponse: errorResponse,
        },
      })
    }

    handleError(dispatch, { error, status })

    return { success: false }
  }
}

function handleError(dispatch, { error, status, message, data }) {
  Sentry.captureException(error)
  console.error('Failed to save setup', error, status, message, data)

  const errorCode = codifyAPIError({ error, status, message, data })
  dispatch({ type: actions.error, payload: errorCode })
}
